import React, { useState, useEffect, useCallback } from "react";
import {
  BeadCrumb,
  Table,
  Typography,
  CustomHeader,
  TextField,
  Loader,
  Tag,
  AnchorMenu,
  DateFilterModal,
  TableFooter,
} from "components";
import ViewTerminal from "./ViewTerminal";
import debounce from "lodash.debounce";
import { terminalStatuses } from "utils/constants";
import {
  isObjectsEqual,
  formatUrlQuery,
  formatFilterPayload,
  formatDateTime,
  truncateString,
  // capitalizeFirstLetter,
} from "utils/helper";
import { ROUTES } from "routes/routePaths";
import { useNavigate } from "react-router-dom";
import { routes } from "routes/apiRoutes";
import { BiCalendar } from "react-icons/bi";
import { FaExchangeAlt } from "react-icons/fa";
import makeRequest from "services/makeRequest";

import "./style.scss";

const ManageTerminals = () => {
  let columns = [
    {
      Header: "S/N",
      Cell: (row) => Number(row.row.id) + 1,
    },
    {
      Header: "OEM Name",
      accessor: "oem",
    },
    {
      Header: "Terminal ID",
      accessor: "terminalId",
    },
    {
      Header: "Serial Number",
      accessor: "serialNumber",
    },
    // {
    //   Header: "Device Type",
    //   accessor: "deviceType",
    //   Cell: (props) => (props.value ? capitalizeFirstLetter(props.value) : ""),
    // },
    {
      Header: "IMEI",
      accessor: "imei",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: (props) => (
        <div className="d-flex justify-content-center">
          {getStatusTag(props.value)}
        </div>
      ),
    },
    {
      Header: "Date Registered",
      accessor: "createDate",
      Cell: (props) => formatDateTime(props.value),
    },

    {
      Header: "Action",
      Cell: ({ row }) => (
        <AnchorMenu anchorRender={() => anchorRender(row.original)} />
      ),
    },
  ];

  const navigate = useNavigate();

  const anchorRender = (value) => {
    return (
      <ul className="py-1" style={{ paddingLeft: "1rem" }}>
        <li className="cursor-pointer" onClick={() => setTerminalToView(value)}>
          <Typography state="primary" variant="body3">
            View Terminal
          </Typography>
        </li>
        {
          //dsable edit if its not approved yet
          value.status.toLowerCase() !== "pending_creation_approval" && (
            <li
              onClick={() =>
                navigate(ROUTES.registerTerminal, {
                  state: { currentTerminal: value },
                })
              }
              className="cursor-pointer"
            >
              <Typography state="primary" variant="body3">
                Update Terminal
              </Typography>
            </li>
          )
        }
      </ul>
    );
  };

  const getStatusTag = (value) => {
    let terminal = terminalStatuses.find((status) => status.mapper === value);

    const { text, bgColor, color } = terminal || {};

    return terminal ? (
      <Tag text={text} backgroundColor={bgColor} textColor={color} />
    ) : (
      value
    );
  };

  const initialFilterState = {
    sort: false,
    number: 1,
    startDate: "",
    endDate: "",
    status: "",
    oem: "",
    size: 10,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const [data, setData] = useState({
    terminals: null,
    loading: false,
  });

  const { startDate, endDate } = filterState;

  const [openDateModal, setOpenDateModal] = useState(false);

  const [terminalToView, setTerminalToView] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilterState({ ...filterState, [name]: value });
  };

  const getTerminalsData = (queryParams) =>
    makeRequest("get", routes.fetchTerminals(queryParams));

  const resetFilter = () => {
    filterState.oem = "";
    setFilterState(initialFilterState);
    loadTerminals();
  };

  const loadTerminals = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setData({ ...data, loading: true });
    try {
      const res = await getTerminalsData(queryParams);
      setData((prev) => ({
        ...prev,
        terminals: res.data.data,
        loading: false,
      }));
    } catch (err) {
      console.log(err);
      setData({ ...data, loading: false });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadWithDebounce = useCallback(
    debounce((oem) => {
      if (oem === "") {
        loadTerminals();
      } else {
        loadTerminals(`oem=${oem}`);
      }
    }, 1000),
    []
  );

  const handleSearch = (e) => {
    let val = e.target.value;
    setFilterState({ ...filterState, oem: val });
    loadWithDebounce(val);
  };

  useEffect(() => {
    loadTerminals();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.number,
    filterState.status,
    filterState.size,
    filterState.sort,
  ]);

  if (filterState.status === "CREATION_REJECTED") {
    columns.splice(5, 0, {
      Header: "Comment",
      Cell: ({ cell }) => {
        return truncateString(cell.row.original.rejectionComment);
      },
    });
  }

  const getCurrentStatus = () =>
    terminalStatuses.find(
      (terminal) => terminal.value.toString() === filterState.status
    );

  return (
    <div className="bg-light pt-3" style={{ paddingBottom: "4rem" }}>
      <BeadCrumb titles={["Terminals", "Manage Terminals"]} />
      <CustomHeader
        title="Manage Terminals"
        titlePosition="center"
        className="mt-4"
      />
      <div className="px-4 terminal-management-container">
        <div className="table-header-actions mt-4 mb-2">
          <div className="d-md-flex cursor-pointer">
            <div className="filter-cta d-none d-sm-block mb-2">
              <Typography state="grey">Filter By</Typography>
            </div>
            <select
              name="sort"
              value={filterState.sort}
              className="table-header-select mb-2"
              onChange={(e) =>
                setFilterState({
                  ...filterState,
                  sort: JSON.parse(e.target.value),
                })
              }
            >
              <option value={true}>Ascending</option>
              <option value={false}>Descending</option>
            </select>
            <div
              className="date-cta mb-2"
              onClick={() => setOpenDateModal(true)}
            >
              <Typography component="span">Select Date</Typography>{" "}
              <BiCalendar style={{ marginBottom: ".2rem" }} />
            </div>
            <select
              name="status"
              value={filterState.status}
              onChange={handleChange}
              className="table-header-select mb-2"
            >
              <option value="">All</option>
              {terminalStatuses.map(({ label, mapper }, key) => (
                <option value={mapper} key={key}>
                  {label}
                </option>
              ))}
            </select>
            {!isObjectsEqual(initialFilterState, filterState) && (
              <div className="reset-filter-cta mb-2" onClick={resetFilter}>
                <Typography state="grey" component="span">
                  Reset Filter
                </Typography>{" "}
                <FaExchangeAlt
                  style={{
                    color: "grey",
                    fontSize: "14px",
                    marginLeft: ".3rem",
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between my-3">
          <div className="d-flex align-items-center">
            <Typography state="primary" variant="subtitle4">
              {!getCurrentStatus() && "All"} Terminals{" "}
              {getCurrentStatus()?.label}
            </Typography>{" "}
            &nbsp; &nbsp;{" "}
            {data.loading && (
              <span className="mt-2">
                <Loader height={20} width={2} />
              </span>
            )}
          </div>
          <div>
            <TextField
              name="oem"
              id="oem"
              value={filterState.oem}
              onChange={handleSearch}
              placeholder={"Search by OEM Name"}
              size="sm"
              required={false}
              searchField
              className="tiny-bordered-textfield"
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={data.terminals?.content}
          centerData
          loading={data.loading}
        />
        {data.terminals?.content?.length ? (
          <TableFooter
            pageSize={filterState.size}
            handleChange={handleChange}
            availableDataLength={data.terminals.content.length}
            totalDataLength={data.terminals.totalElements}
            handlePageClick={(pageNo) =>
              setFilterState({ ...filterState, number: pageNo })
            }
          />
        ) : (
          ""
        )}
      </div>
      <DateFilterModal
        openDateModal={openDateModal}
        setOpenDateModal={setOpenDateModal}
        handleChange={handleChange}
        startDate={startDate}
        endDate={endDate}
        onButtonClick={() => {
          setOpenDateModal(false);
          loadTerminals();
        }}
      />
      <ViewTerminal
        terminalToView={terminalToView}
        handleClose={() => setTerminalToView(null)}
      />
    </div>
  );
};

export default ManageTerminals;
